import { useTranslation } from "react-i18next";
import type { CompanyResult } from "@/types/api";

// ── Color helpers ─────────────────────────────────────────────────────────────

function getLlmPillColor(rawScore: number): string {
  if (rawScore < 40)
    return "bg-red-500/10 text-red-400 border border-red-500/20";
  if (rawScore < 70)
    return "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20";
  return "bg-green-500/10 text-green-400 border border-green-500/20";
}

function sourceLabel(type: string): string {
  if (type === "google_maps") return "Maps";
  if (type === "local_pack") return "Local";
  return "Web";
}

// ── KI-Einschätzung (shared between fallback and full breakdown) ─────────────

function KiEinschaetzung({ result }: { result: CompanyResult }) {
  const { t } = useTranslation("results");
  const hasLlm = result.llm_relevance_score != null;
  const pillColor = hasLlm ? getLlmPillColor(result.llm_relevance_score!) : "";

  return (
    <>
      <div className="flex items-center gap-[0.4rem] mb-[0.35rem]">
        <div className="w-4 h-4 rounded bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-[0.5rem] text-purple-400 font-bold shrink-0">
          KI
        </div>
        <span className="text-[0.68rem] font-semibold text-muted-foreground uppercase tracking-wider">
          {t("confidence.kiLabel")}
        </span>
        {hasLlm && (
          <span
            className={`ml-auto text-[0.62rem] font-bold px-[7px] py-[1px] rounded-full tabular-nums ${pillColor}`}
          >
            {result.llm_relevance_score} / 100
          </span>
        )}
      </div>
      {hasLlm ? (
        result.llm_relevance_reason ? (
          <p className="text-[0.76rem] text-muted-foreground leading-relaxed">
            {result.llm_relevance_reason}
          </p>
        ) : null
      ) : (
        <p className="text-[0.74rem] text-muted-foreground/60 italic leading-relaxed">
          {t("confidence.noAnalysis")}
        </p>
      )}
    </>
  );
}

// ── Simple fallback when no breakdown data is available ───────────────────────

function SimpleFallback({ result }: { result: CompanyResult }) {
  return (
    <div className="bg-card border border-border rounded-[10px] p-4">
      <KiEinschaetzung result={result} />
    </div>
  );
}

const FACTOR_GRID_COLS = "118px 30px 1fr 32px";

// ── Factor row ────────────────────────────────────────────────────────────────

interface FactorRowProps {
  label: string;
  status: string;
  barWidth: number; // 0–100
  barColor: string; // Tailwind bg-* class
  points: string; // "+10", "—", "-5"
  pointsColor?: string; // Tailwind text-* class
  isBase?: boolean;
  subnote?: React.ReactNode;
}

function FactorRow({
  label,
  status,
  barWidth,
  barColor,
  points,
  pointsColor = "text-muted-foreground",
  isBase = false,
  subnote,
}: FactorRowProps) {
  return (
    <>
      <div
        className="grid items-center text-[0.72rem] gap-x-[0.4rem]"
        style={{ gridTemplateColumns: FACTOR_GRID_COLS }}
      >
        {/* Label */}
        <span
          className={`whitespace-nowrap ${isBase ? "italic text-muted-foreground/60" : "text-muted-foreground"}`}
        >
          {label}
        </span>

        {/* Status */}
        <span
          className={`text-[0.68rem] text-center ${
            status === "✓"
              ? "text-green-500"
              : status === "✗"
                ? "text-muted-foreground/60"
                : "text-muted-foreground"
          }`}
        >
          {status}
        </span>

        {/* Mini-bar — 3px height per mockup */}
        <div className="h-[3px] bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${barColor}`}
            style={{ width: `${Math.min(barWidth, 100)}%` }}
          />
        </div>

        {/* Points */}
        <span
          className={`text-[0.68rem] text-right tabular-nums font-medium ${pointsColor}`}
        >
          {points}
        </span>
      </div>

      {subnote && (
        <div
          className="grid"
          style={{
            gridTemplateColumns: FACTOR_GRID_COLS,
            marginTop: "-0.1rem",
          }}
        >
          <div className="col-start-2 col-span-3 text-[0.62rem] text-muted-foreground/60 leading-tight">
            {subnote}
          </div>
        </div>
      )}
    </>
  );
}

function FactorsDivider() {
  return (
    <hr className="border-0 border-t border-dashed border-border my-[0.15rem]" />
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface ConfidenceBreakdownProps {
  result: CompanyResult;
}

export function ConfidenceBreakdown({ result }: ConfidenceBreakdownProps) {
  const { t } = useTranslation("results");
  if (!result.confidence_breakdown) {
    return <SimpleFallback result={result} />;
  }

  const bd = result.confidence_breakdown;

  // ── Source label ────────────────────────────────────────────────────────────
  const srcLabel = sourceLabel(bd.source.type);
  const srcBarWidth = Math.round(bd.source.value * 100);

  // ── Completeness ────────────────────────────────────────────────────────────
  const hasName = bd.completeness.company_name;
  const hasAddr = bd.completeness.address;
  const hasPhone = bd.completeness.phone;
  const hasEmail = bd.completeness.email;

  // ── Keywords ────────────────────────────────────────────────────────────────
  const kwStatus = `${bd.keywords.matched}/${bd.keywords.total}`;
  const kwBarWidth = Math.round(bd.keywords.value * 100);
  const kwPoints =
    bd.keywords.value > 0 ? `+${Math.round(bd.keywords.value * 100)}` : "—";

  // ── Reputation ──────────────────────────────────────────────────────────────
  const rep = bd.reputation;
  const repDelta = rep.delta;
  const repBarWidth = Math.min(Math.round(Math.abs(repDelta) * 100), 100);
  const repBarColor = repDelta >= 0 ? "bg-green-500" : "bg-red-500";
  const repStatus =
    repDelta !== 0
      ? repDelta > 0
        ? `+${repDelta.toFixed(2)}`
        : repDelta.toFixed(2)
      : "—";
  const repPoints =
    repDelta > 0
      ? `+${Math.round(repDelta * 100)}`
      : repDelta < 0
        ? `${Math.round(repDelta * 100)}`
        : "—";
  const repPointsColor =
    repDelta > 0
      ? "text-green-500"
      : repDelta < 0
        ? "text-red-500"
        : "text-muted-foreground";

  const repSubnote =
    repDelta !== 0
      ? repDelta > 0
        ? t("confidence.repPositive")
        : t("confidence.repNegative")
      : t("confidence.repNone");

  return (
    <div className="bg-card border border-border rounded-[10px] p-4">
      {/* ── KI-Einschätzung section ───────────────────────────────────────── */}
      <div className="mb-3">
        <KiEinschaetzung result={result} />
      </div>

      {/* ── Factor rows ───────────────────────────────────────────────────── */}
      <div className="flex flex-col mb-3 gap-[0.3rem]">
        {/* Basis-Score (dim) — derived from breakdown data */}
        <FactorRow
          label={t("confidence.factorBase")}
          status="—"
          barWidth={Math.round(bd.base * 100)}
          barColor="bg-zinc-600"
          points={`+${Math.round(bd.base * 100)}`}
          pointsColor="text-muted-foreground/60"
          isBase
        />

        <FactorsDivider />

        {/* Datenquelle */}
        <FactorRow
          label={t("confidence.factorSource")}
          status={srcLabel}
          barWidth={srcBarWidth}
          barColor="bg-blue-500"
          points={`+${srcBarWidth}`}
          pointsColor="text-green-500"
        />

        {/* Firmenname */}
        <FactorRow
          label={t("confidence.factorCompanyName")}
          status={hasName ? "✓" : "✗"}
          barWidth={hasName ? 10 : 0}
          barColor="bg-green-500"
          points={hasName ? "+10" : "—"}
          pointsColor={hasName ? "text-green-500" : "text-muted-foreground"}
        />

        {/* Adresse */}
        <FactorRow
          label={t("confidence.factorAddress")}
          status={hasAddr ? "✓" : "✗"}
          barWidth={hasAddr ? 10 : 0}
          barColor="bg-green-500"
          points={hasAddr ? "+10" : "—"}
          pointsColor={hasAddr ? "text-green-500" : "text-muted-foreground"}
        />

        {/* Telefon */}
        <FactorRow
          label={t("confidence.factorPhone")}
          status={hasPhone ? "✓" : "✗"}
          barWidth={hasPhone ? 5 : 0}
          barColor="bg-green-500"
          points={hasPhone ? "+5" : "—"}
          pointsColor={hasPhone ? "text-green-500" : "text-muted-foreground"}
        />

        {/* E-Mail */}
        <FactorRow
          label={t("confidence.factorEmail")}
          status={hasEmail ? "✓" : "✗"}
          barWidth={hasEmail ? 5 : 0}
          barColor="bg-green-500"
          points={hasEmail ? "+5" : "—"}
          pointsColor={hasEmail ? "text-green-500" : "text-muted-foreground"}
        />

        {/* Suchbegriff-Treffer */}
        <FactorRow
          label={t("confidence.factorKeywords")}
          status={kwStatus}
          barWidth={kwBarWidth}
          barColor="bg-yellow-500"
          points={kwPoints}
          pointsColor={
            bd.keywords.value > 0 ? "text-green-500" : "text-muted-foreground"
          }
        />

        <FactorsDivider />

        {/* Nutzer-Feedback */}
        <FactorRow
          label={t("confidence.factorReputation")}
          status={repStatus}
          barWidth={repBarWidth}
          barColor={repBarColor}
          points={repPoints}
          pointsColor={repPointsColor}
          subnote={repSubnote}
        />
      </div>
    </div>
  );
}
